import { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { ChangeDate, GridRow, UpcomingFee } from "./api";
import {
  addDays, AS_OF_COPY as T, AS_OF_HORIZON_DAYS, AS_OF_SETTLE_MS, changeDateLabel, changeText, fieldDate, noticeText, withinHorizon, type PickFn,
} from "./as_of";
import { humanDate } from "./format";
import { useDebounced } from "@/lib/useDebounced";

/** The as-of control in the toolbar (v26 "As of [date] today", ux-design U1 and U9): any date from the run date to twelve months
 * ahead, with what is in force on it. Picking the run date or "Back to today" is today. The field keeps a draft of its own: in
 * the browser every part typed or emptied changes its value, so the draft follows the field and only a whole date within
 * range, rested for `AS_OF_SETTLE_MS`, is written; an empty or partial field writes nothing and shows the date on screen again
 * when it loses focus (review round 2: a field bound to the URL alone put back every partial value and read an emptied part
 * as today). `unread`: a date is asked and no grid of it is read yet (pending, refused or failed), so nothing is said about
 * the set in force and Back to today sits in the alert alone (review rounds 1 and 2). The control changes nothing (AGENTS
 * rule 11: `data-nonmutating`), so a view-only person picks a date too. */
export function AsOfControl({ asOf, runDate, inForceFrom, unread, onChange }: {
  asOf: string | null; runDate: string | null; inForceFrom: string | null; unread: boolean; onChange: (next: string | null) => void;
}) {
  const shown = asOf ?? runDate ?? "";
  const [draft, setDraft] = useState(shown);
  // A date picked elsewhere (a link, the next-fee button, Back to today, the URL) replaces what the field holds.
  useEffect(() => setDraft(shown), [shown]);
  const settled = useDebounced(draft, AS_OF_SETTLE_MS);
  useEffect(() => {
    if (settled === shown) return;
    const next = fieldDate(settled, runDate);
    if (next !== undefined) onChange(next);
  }, [settled]);
  const note = unread ? null : asOf === null ? T.today : inForceFrom !== null ? T.inForce(humanDate(inForceFrom)) : T.noneInForce;
  return (
    <div role="group" aria-label={T.label} className="flex flex-wrap items-center gap-2">
      <Label htmlFor="pp-as-of" className="text-sm text-ks-grey-1">{T.label}</Label>
      <Input id="pp-as-of" type="date" className="w-40" title={T.pickTitle} data-nonmutating value={draft}
        min={runDate ?? undefined} max={runDate === null ? undefined : addDays(runDate, AS_OF_HORIZON_DAYS)}
        onChange={(e) => setDraft(e.target.value)} onBlur={() => { if (fieldDate(draft, runDate) === undefined) setDraft(shown); }} />
      {note && <span className="text-sm text-ks-grey-1">{note}</span>}
      {asOf !== null && !unread && <Button size="sm" variant="outline" data-nonmutating onClick={() => onChange(null)}>{T.backToToday}</Button>}
    </div>
  );
}

/** "Figures move on:" (v26): one link per change date of the run, named by what changes on it, the one on screen pressed; with
 * none dated, one sentence saying every date reads today's figures. Nothing on an API before C2 v1.26. */
export function MovesOnLinks({ dates, asOf, onPick }: { dates: ChangeDate[] | undefined; asOf: string | null; onPick: (date: string) => void }) {
  if (dates === undefined) return null;
  if (dates.length === 0) return <p className="text-xs text-ks-grey-1">{T.noDates}</p>;
  return (
    <div role="group" aria-label={T.movesOn} className="flex flex-wrap items-center gap-1">
      <span className="text-xs text-ks-grey-1">{T.movesOn}</span>
      {dates.map((d) => (
        <Button key={d.date} size="sm" variant={d.date === asOf ? "default" : "ghost"} aria-pressed={d.date === asOf} data-nonmutating onClick={() => onPick(d.date)}>
          {changeDateLabel(d)}
        </Button>
      ))}
    </div>
  );
}

/** The "moves" marker in a Fulfil cell on a picked date: what differs from today's figures on that row, each as C2 wrote it
 * (the badge's title lists them all, the line under it the first). Nothing on a row that does not move. */
export function MovesLines({ row, currency }: { row: GridRow; currency: string | null }) {
  const changes = row.changes ?? [];
  if (changes.length === 0) return null;
  return (
    <div className="mt-1">
      <Badge variant="outline" className="border-ks-purple text-ks-purple" title={changes.map((c) => changeText(c, currency)).join("; ")}>{T.moves}</Badge>
      <div className="text-xs text-ks-grey-1">{changeText(changes[0], currency)}{changes.length > 1 ? ` · ${T.more(changes.length - 1)}` : ""}</div>
    </div>
  );
}

/** A button of the drawer that reads a date's figures before it shows them (C1 v1.28, `readThenPick` through `PickContext`):
 * held and named while it reads; a read that failed keeps the drawer where it is and says so in an alert (AGENTS rule 14), and
 * the button can be pressed again. `alive` tells the pick whether this drawer is still open, so a pick that outlives it writes
 * nothing (set again on every mount, as an effect may run twice). It changes nothing (AGENTS rule 11: `data-nonmutating`). */
function PickButton({ date, label, reading, unread, onPick }: { date: string | null; label: string; reading: string; unread: string; onPick: PickFn }) {
  const [state, setState] = useState<"idle" | "reading" | "unread">("idle");
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const press = () => {
    setState("reading");
    void onPick(date, () => alive.current).then((outcome) => { if (alive.current) setState(outcome === "unread" ? "unread" : "idle"); });
  };
  return (
    <>
      <Button size="sm" variant="outline" className="mt-2 border-ks-purple text-ks-purple" data-nonmutating disabled={state === "reading"} aria-busy={state === "reading"}
        onClick={press}>
        {state === "reading" ? reading : label}
      </Button>
      {state === "unread" && <p role="alert" className="mt-1 text-sm">{unread}</p>}
    </>
  );
}

/** The drawer's notice of the row's next fee change on today's figures (C1 v1.28, c1-drawer-fee-change; the table row's line
 * is the same change): the date, what changes as `noticeText` words it, that the figures under it are today's, and a button
 * that reads the date's figures before it shows them. A change dated past C2's twelve months says so and has no button, as the
 * table row's line; a date on or before the run date on screen (a market still on an older run, in the All view) has no button
 * and no such sentence (review round 2); without `onPick` (a sheet that cannot pick a date) there is no button either. */
export function UpcomingFeeNotice({ fee, today, currency, runDate, onPick }: {
  fee: UpcomingFee; today: string | null; currency: string | null; runDate: string | null; onPick?: PickFn;
}) {
  const day = humanDate(fee.effective_from);
  const far = runDate !== null && fee.effective_from > addDays(runDate, AS_OF_HORIZON_DAYS);
  return (
    <section aria-label={T.noticeLabel} className="mt-3 rounded-md border border-ks-purple p-3 text-sm">
      <p className="font-semibold text-ks-purple">{T.noticeTitle(day)}</p>
      <p className="text-ks-ink">{noticeText(fee, today, currency)}</p>
      <p className="text-xs text-ks-grey-1">{far ? T.noticeFar : T.noticeToday}</p>
      {onPick && withinHorizon(fee.effective_from, runDate) && <PickButton date={fee.effective_from} label={T.noticeSee(day)} reading={T.noticeReading(day)} unread={T.unreadDate(day)} onPick={onPick} />}
    </section>
  );
}

/** The drawer's way back on a picked date (C1 v1.28, owner ruling A1 of review round 1): the date on screen and Back to today,
 * which reads today's figures before it shows them, so the drawer stays open on the row. The toolbar's Back to today sits behind
 * the sheet's overlay. Without `onPick` the line names the date alone. */
export function DrawerDateLine({ date, onPick }: { date: string; onPick?: PickFn }) {
  return (
    <section aria-label={T.drawerLabel} className="mt-3 rounded-md border border-ks-purple p-3 text-sm">
      <p className="text-ks-ink">{T.drawerOn(humanDate(date))}</p>
      {onPick && <PickButton date={null} label={T.backToToday} reading={T.backReading} unread={T.unreadToday} onPick={onPick} />}
    </section>
  );
}
